// src/ProfilePage.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Avatar,
  Typography,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';

import ProfileCourtesy       from './ProfileCourtesy';
import ProfileVetting        from './ProfileVetting';
import RefreshIcon            from '@mui/icons-material/Refresh';
import EmailIcon              from '@mui/icons-material/Email';
import FormatBoldIcon         from '@mui/icons-material/FormatBold';
import FormatItalicIcon       from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon   from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LinkIcon               from '@mui/icons-material/Link';
import AttachFileIcon         from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon    from '@mui/icons-material/InsertDriveFile';
import CloseIcon              from '@mui/icons-material/Close';
import DraftsIcon             from '@mui/icons-material/Drafts';
import SendIcon               from '@mui/icons-material/Send';

import { useParams, useNavigate } from 'react-router-dom';
import BlockIcon from '@mui/icons-material/Block';
import UnblockIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MessageIcon from '@mui/icons-material/Message';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { supabase } from './supabaseClient';
import { openLegalDoc } from './legalDocUrl';
import ContactUsWidget from './ContactUsWidget';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

import { brand, ink, status, surface } from './designTokens';
import { prettyStatus } from './labels';
const SCOPES = ["https://graph.microsoft.com/Mail.Send.Shared"];

const tabs = [
  { label: 'Support',      type: 'support' },
  { label: 'Bookings',     type: 'table', name: 'bookings' },
  { label: 'Chats',        type: 'chats' },
  { label: 'Payments',     type: 'table', name: 'payments' },
  { label: 'Payouts',      type: 'payouts' },
  { label: 'Closet',       type: 'table', name: 'Closet Items' },
  { label: 'Ratings',      type: 'ratings' },
  { label: 'Legal Docs',   type: 'legal' },
  { label: 'Refunds',      type: 'refunds' },
  { label: 'Send Email',   type: 'email' },
];

export default function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [loading, setLoading]         = useState(false);
  const [profile, setProfile]         = useState(null);
  const [tabIndex, setTabIndex]       = useState(0);
  const [relatedRows, setRelatedRows] = useState([]);
  const [emailLog, setEmailLog]       = useState([]);
  const [emailForm, setEmailForm]     = useState({
    to: '',
    cc: '',
    subject: '',
    body: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [snack, setSnack]             = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isBlocked, setIsBlocked] = useState(false);
  // New state for redesigned profile view
  const [stats, setStats] = useState({
    lifetimeValue: 0,
    totalBookings: 0,
    closetItems: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [bookingActivityData, setBookingActivityData] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [legalDocs, setLegalDocs] = useState([]);
  const [legalCompliance, setLegalCompliance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [earningsSummary, setEarningsSummary] = useState({ available: 0, processing: 0, paid: 0, availableIds: [] });
  const [defaultPayoutMethod, setDefaultPayoutMethod] = useState(null);
  const [payoutActionLoading, setPayoutActionLoading] = useState(false);
  const [payoutActionError, setPayoutActionError] = useState(null);
  const [refundableBookings, setRefundableBookings] = useState([]);
  const [refundActionLoading, setRefundActionLoading] = useState(null);
  const [refundActionError, setRefundActionError] = useState(null);

  const emailTabIndex = tabs.findIndex(t => t.type === 'email');
  const contactTab = tabs.find(t => t.name === 'Contact Us');

  const toArray = useCallback((val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch (_) {
        // not JSON, fall through
      }
      return val.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
  }, []);

  const buildPublicUrl = useCallback((bucket, path) => {
    if (!path) return null;
    const trimmed = path.replace(/^https?:\/\/[^/]+\/storage\/v1\/object\/public\//, '');
    if (trimmed.startsWith('http')) return trimmed;
    const clean = trimmed.replace(/^\/+/, '');
    const base = process.env.REACT_APP_SUPABASE_URL;
    return `${base}/storage/v1/object/public/${bucket}/${clean}`;
  }, []);

  const normalizeProfileImage = useCallback((img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    const clean = img.startsWith('profiles/') ? img : `profiles/${img}`;
    return buildPublicUrl('profile-images', clean);
  }, [buildPublicUrl]);

  // Fetch ratings/reviews for stylist
  const fetchRatings = useCallback(async (stylistPhoneNumber) => {
    try {
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('stylist_phone_number', stylistPhoneNumber)
        .order('created_at', { ascending: false });

      if (reviewsError) {
        console.error('Error fetching ratings:', reviewsError);
        setRatings([]);
        setAverageRating(0);
        return;
      }

      if (!reviewsData || reviewsData.length === 0) {
        setRatings([]);
        setAverageRating(0);
        return;
      }

      // Fetch client profiles for reviews
      const clientPhones = [...new Set(reviewsData.map(r => r.phone_number).filter(Boolean))];
      const clientMap = new Map();

      if (clientPhones.length > 0) {
        const { data: clients, error: clientsError } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, phone_number, email, profile_image')
          .in('phone_number', clientPhones);

        if (!clientsError && clients) {
          clients.forEach(client => {
            clientMap.set(client.phone_number, {
              ...client,
              profile_image_url: normalizeProfileImage(client.profile_image),
            });
          });
        }
      }

      // Enrich reviews with client profile data
      const enrichedRatings = reviewsData.map(review => ({
        ...review,
        client: clientMap.get(review.phone_number) || null,
      }));

      setRatings(enrichedRatings);

      // Calculate average rating
      const ratingsArray = reviewsData.map(r => parseFloat(r.rating) || 0).filter(r => r > 0);
      const avg = ratingsArray.length > 0
        ? ratingsArray.reduce((sum, r) => sum + r, 0) / ratingsArray.length
        : 0;
      setAverageRating(avg);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      setRatings([]);
      setAverageRating(0);
    }
  }, [normalizeProfileImage]);

  // Fetch profile
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('Profiles')
      .select('*')
      .eq('id', id)
      .single();
    setLoading(false);
    if (error) return console.error(error);

    // Get profile image URL (normalize different path shapes)
    data.profile_image_url = normalizeProfileImage(data.profile_image) || data.profile_image_url || null;

    // Normalize additional images to full URLs
    if (data.additional_images) {
      const arr = toArray(data.additional_images);
      data.additional_images = arr.map(img => {
        if (!img) return null;
        if (img.startsWith('http')) return img;
        const clean = img.replace(/^additional_images\//, '').replace(/^\/+/, '');
        return buildPublicUrl('additional_images', clean);
      }).filter(Boolean);
    }

    setProfile(data);
    setEmailForm(f => ({ ...f, to: data.email }));
    fetchEmailLog(data.id);

    // Fetch stats, bookings, and subscription data
    if (data.id) {
      await fetchProfileStats(data.id, data.phone_number);
      await fetchRecentBookings(data.id);
      await fetchSubscription(data.id, data.phone_number);
      await fetchBookingActivity(data.id);
      
      // Fetch ratings if user is a stylist
      if (data.user_type === 'Stylist' && data.phone_number) {
        await fetchRatings(data.phone_number);
      }

      if (String(data.user_type || '').toLowerCase() === 'stylist') {
        await fetchLegalDocs(data.id);
      } else {
        setLegalDocs([]);
        setLegalCompliance(null);
      }
    }

    // Check if user is blocked
    if (data.phone_number) {
      const { data: blockedData } = await supabase
        .from('blocked_users')
        .select('*')
        .eq('blocked_phone_number', data.phone_number)
        .maybeSingle();
      setIsBlocked(!!blockedData);

      // Preload Contact Us
      if (contactTab) {
        fetchRelated(data.phone_number, contactTab);
      }
      // Preload Bookings
      const bookingsTab = tabs.find(t => t.name === 'bookings');
      if (bookingsTab) {
        fetchRelated(data.phone_number, bookingsTab);
      }
    }
  }, [id, contactTab, normalizeProfileImage, toArray, buildPublicUrl, fetchRatings]);

  // Fetch profile stats (lifetime value, bookings count, closet items count)
  const fetchProfileStats = async (profileId, phoneNumber) => {
    try {
      // Fetch bookings to calculate lifetime value and count. This profile
      // could be the client OR the stylist on a booking — client_id alone
      // missed every booking for a stylist's own profile.
      const { data: bookings } = await supabase
        .from('bookings')
        .select('*')
        .or(`client_id.eq.${profileId},stylist_profile_id.eq.${profileId}`);
      
      // Only count money that was actually paid — summing every booking
      // regardless of payment_completed (cancelled, pending, unpaid) badly
      // overstated this figure. Total Bookings still counts all of them;
      // that stat is booking volume, not revenue.
      const lifetimeValue = bookings?.reduce((sum, b) => {
        if (!b.payment_completed) return sum;
        return sum + (parseFloat(b.price) || 0);
      }, 0) || 0;

      const totalBookings = bookings?.length || 0;

      // Fetch closet items count
      const { count: closetCount } = await supabase
        .from('Closet Items')
        .select('*', { count: 'exact', head: true })
        .eq('phone_number', phoneNumber);

      setStats({
        lifetimeValue,
        totalBookings,
        closetItems: closetCount || 0,
      });
    } catch (error) {
      console.error('Error fetching profile stats:', error);
    }
  };

  // Fetch recent bookings
  const fetchRecentBookings = async (profileId) => {
    try {
      const { data: bookingsData } = await supabase
        .from('bookings')
        .select('*')
        .or(`client_id.eq.${profileId},stylist_profile_id.eq.${profileId}`)
        .order('start_ts', { ascending: false })
        .limit(4);

      if (bookingsData) {
        const stylistIds = [...new Set(bookingsData.map(b => b.stylist_profile_id).filter(Boolean))];
        const stylistPhones = [...new Set(bookingsData.map(b => b.stylists_phone_number || b.stylist_phone_number || b.stylist_phone).filter(Boolean))];

        const stylistMapById = new Map();
        const stylistMapByPhone = new Map();

        if (stylistIds.length > 0) {
          const { data: stylistsById } = await supabase
            .from('Profiles')
            .select('id, first_name, last_name, phone_number')
            .in('id', stylistIds);
          
          if (stylistsById) {
            stylistsById.forEach(s => {
              if (s.id) stylistMapById.set(s.id, s);
              if (s.phone_number) stylistMapByPhone.set(s.phone_number, s);
            });
          }
        }

        if (stylistPhones.length > 0) {
          const missingPhones = stylistPhones.filter(p => !stylistMapByPhone.has(p));
          if (missingPhones.length > 0) {
            const { data: stylistsByPhone } = await supabase
              .from('Profiles')
              .select('id, first_name, last_name, phone_number')
              .in('phone_number', missingPhones);

            if (stylistsByPhone) {
              stylistsByPhone.forEach(s => {
                if (s.id) stylistMapById.set(s.id, s);
                if (s.phone_number) stylistMapByPhone.set(s.phone_number, s);
              });
            }
          }
        }

        const enriched = bookingsData.map(booking => {
          const stylistPhone = booking.stylists_phone_number || booking.stylist_phone_number || booking.stylist_phone;
          const stylistProfile = booking.stylist_profile_id
            ? stylistMapById.get(booking.stylist_profile_id)
            : (stylistPhone ? stylistMapByPhone.get(stylistPhone) : null);

          return {
            ...booking,
            stylist: stylistProfile || null,
            stylist_phone_number: stylistPhone || stylistProfile?.phone_number || null,
          };
        });

        setRecentBookings(enriched);
      }
    } catch (error) {
      console.error('Error fetching recent bookings:', error);
    }
  };

  // Fetch subscription data from user_subscriptions + plan from Subscriptions table
  const fetchSubscription = async (_profileId, phoneNumber) => {
    try {
      if (!phoneNumber) {
        setSubscription(null);
        return;
      }

      const { data: userSubs, error: userSubsError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('phone_number', phoneNumber)
        .order('start_date', { ascending: false });

      if (userSubsError) throw userSubsError;

      const activeSub = (userSubs || []).find(
        (sub) => (sub.status || '').toLowerCase() === 'active'
      ) || (userSubs && userSubs[0]);

      if (!activeSub) {
        setSubscription(null);
        return;
      }

      let plan = null;
      if (activeSub.subscription_id) {
        const { data: planData, error: planError } = await supabase
          .from('Subscriptions')
          .select('*')
          .eq('id', activeSub.subscription_id)
          .single();

        if (planError) {
          console.error('Error fetching subscription plan:', planError);
        } else {
          plan = planData;
        }
      }

      const price =
        activeSub?.renewal_period === 'yearly'
          ? plan?.yearly_price
          : plan?.monthly_price;

      setSubscription({
        ...activeSub,
        plan,
        plan_name: plan?.display_name || 'Subscription',
        price,
      });
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(null);
    }
  };

  // Fetch booking activity data for chart (last 6 months)
  const fetchBookingActivity = async (profileId) => {
    try {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      
      const { data: bookings } = await supabase
        .from('bookings')
        .select('start_ts, status')
        .eq('client_id', profileId)
        .gte('start_ts', sixMonthsAgo.toISOString());

      // Group by month
      const monthlyData = {};
      const monthNames = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      bookings?.forEach(booking => {
        const date = new Date(booking.start_ts);
        const monthKey = monthNames[date.getMonth()] || date.toLocaleDateString('en-US', { month: 'short' });
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
      });

      const chartData = monthNames.map(month => ({
        month,
        bookings: monthlyData[month] || 0,
      }));

      setBookingActivityData(chartData);
    } catch (error) {
      console.error('Error fetching booking activity:', error);
    }
  };


  const fetchPayouts = async (phoneNumber) => {
    try {
      const { data, error } = await supabase
        .from('payout_requests')
        .select('*')
        .eq('stylist_phone_number', phoneNumber)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setPayouts(data || []);
    } catch (err) {
      console.error('Error fetching payouts:', err);
      setPayouts([]);
    }

    try {
      const [{ data: earnings }, { data: methods }] = await Promise.all([
        supabase.from('stylist_earnings').select('id, amount, status').eq('stylist_phone_number', phoneNumber),
        supabase.from('stylist_payout_methods').select('*').eq('stylist_phone_number', phoneNumber).eq('is_active', true).order('is_default', { ascending: false }),
      ]);

      const available = (earnings || []).filter(e => e.status === 'available');
      const processing = (earnings || []).filter(e => e.status === 'processing');
      const paid = (earnings || []).filter(e => e.status === 'paid');
      setEarningsSummary({
        available: available.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
        processing: processing.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
        paid: paid.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0),
        availableIds: available.map(e => e.id),
      });
      setDefaultPayoutMethod((methods || []).find(m => m.is_default) || (methods || [])[0] || null);
    } catch (err) {
      console.error('Error fetching earnings summary:', err);
      setEarningsSummary({ available: 0, processing: 0, paid: 0, availableIds: [] });
      setDefaultPayoutMethod(null);
    }
  };

  // Admin-initiated payout: submits a payout_requests row on the stylist's
  // behalf (same shape the app itself writes) and immediately approves it
  // through process-payout-request — reusing its Stripe Connect / Plaid
  // transfer logic rather than duplicating it here.
  const handlePayNow = async () => {
    if (!profile?.phone_number || !defaultPayoutMethod || earningsSummary.availableIds.length === 0) return;
    setPayoutActionLoading(true);
    setPayoutActionError(null);
    try {
      const { data: inserted, error: insertError } = await supabase
        .from('payout_requests')
        .insert({
          stylist_phone_number: profile.phone_number,
          payout_method_id: defaultPayoutMethod.id,
          earnings_ids: earningsSummary.availableIds,
          total_amount: earningsSummary.available,
          status: 'pending',
        })
        .select()
        .single();
      if (insertError) throw insertError;

      await supabase.from('stylist_earnings').update({ status: 'processing' }).in('id', earningsSummary.availableIds);

      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/process-payout-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ request_id: inserted.id, action: 'approve', admin_notes: 'Paid out from stylist profile' }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to process payout');
      }

      await fetchPayouts(profile.phone_number);
    } catch (err) {
      console.error('Error paying out stylist:', err);
      setPayoutActionError(err.message || 'Failed to pay out stylist');
    } finally {
      setPayoutActionLoading(false);
    }
  };

  const fetchRefundableBookings = async (clientId) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('id, price, currency, status, payment_completed, start_ts, service_title, created_at')
        .eq('client_id', clientId)
        .eq('payment_completed', true)
        .order('start_ts', { ascending: false });
      if (error) throw error;
      setRefundableBookings(data || []);
    } catch (err) {
      console.error('Error fetching refundable bookings:', err);
      setRefundableBookings([]);
    }
  };

  const handleIssueRefund = async (bookingId) => {
    if (!window.confirm('Issue a full refund for this booking? This cannot be undone.')) return;
    setRefundActionLoading(bookingId);
    setRefundActionError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/issue-refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ booking_id: bookingId, reason: 'requested_by_customer' }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to issue refund');
      }
      await fetchRefundableBookings(profile.id);
    } catch (err) {
      console.error('Error issuing refund:', err);
      setRefundActionError(err.message || 'Failed to issue refund');
    } finally {
      setRefundActionLoading(null);
    }
  };

  const fetchSupportTickets = async (phoneNumber, profileId) => {
    try {
      // Try fetching by created_by (UUID) first if available
      let query = supabase.from('support_tickets').select('*');
      
      if (profileId) {
        query = query.or(`created_by.eq.${profileId},email.eq.${profile.email || 'nomatch'}`);
      } else {
        query = query.eq('phone_number', phoneNumber);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      setSupportTickets(data || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setSupportTickets([]);
    }
  };

  const fetchLegalDocs = async (profileId) => {
    try {
      const [{ data: docs, error: docsError }, { data: complianceRows, error: complianceError }] = await Promise.all([
        supabase
          .from('stylist_agreements')
          .select('id, agreement_type, agreement_version, provider, status, sent_at, viewed_at, signed_at, signed_pdf_path, created_at')
          .eq('stylist_id', profileId)
          .order('created_at', { ascending: false }),
        supabase
          .from('stylist_compliance_status')
          .select('stylist_id, agreement_status, identity_status, payout_status, updated_at')
          .eq('stylist_id', profileId)
          .maybeSingle(),
      ]);

      if (docsError) throw docsError;
      if (complianceError) throw complianceError;

      setLegalDocs(docs || []);
      setLegalCompliance(complianceRows || null);
    } catch (error) {
      console.error('Error fetching legal docs:', error);
      setLegalDocs([]);
      setLegalCompliance(null);
    }
  };

  // Fetch related rows
  const fetchRelated = async (phone, tab) => {
    setLoading(true);
    let res;
    if (tab.type === 'chats') {
      res = await supabase
        .from('Chats')
        .select('*')
        .or(`sender_phone_number.eq.${phone},receiver_phone_number.eq.${phone}`)
        .order('created_at',{ ascending:false });
    } else if (tab.name === 'bookings') {
      // For bookings, we need to join with Profiles to find by phone
      // First get the profile ID from phone
      const { data: profileData } = await supabase
        .from('Profiles')
        .select('id')
        .eq('phone_number', phone)
        .maybeSingle();
      
      if (profileData?.id) {
        // Fetch bookings without foreign key join to avoid RLS recursion.
        // This profile could be the client OR the stylist on a booking —
        // filtering on client_id alone meant a stylist's own profile never
        // showed any of their bookings (they're stylist_profile_id, never
        // client_id, on their own work).
        const { data: bookingsData, error: bookingsError } = await supabase
          .from('bookings')
          .select('*')
          .or(`client_id.eq.${profileData.id},stylist_profile_id.eq.${profileData.id}`)
          .order('start_ts',{ ascending:false });
        
        if (bookingsError) {
          res = { data: [], error: bookingsError };
        } else {
          // Fetch client and stylist profiles separately
          const clientIds = [...new Set(bookingsData?.map(b => b.client_id).filter(Boolean) || [])];
          const stylistIds = [...new Set(bookingsData?.map(b => b.stylist_profile_id).filter(Boolean) || [])];
          const allIds = [...new Set([...clientIds, ...stylistIds])];
          
          const profileMap = new Map();
          if (allIds.length > 0) {
            const { data: profiles } = await supabase
              .from('Profiles')
              .select('id, first_name, last_name, phone_number')
              .in('id', allIds);
            
            if (profiles) {
              profiles.forEach(p => profileMap.set(p.id, p));
            }
          }

          const stylistPhones = [...new Set(bookingsData.map(b => b.stylists_phone_number || b.stylist_phone_number || b.stylist_phone).filter(Boolean))];
          const clientPhones  = [...new Set(bookingsData.map(b => b.client_phone_number).filter(Boolean))];
          const phoneMap = new Map();

          const phonesToFetch = [...new Set([...stylistPhones, ...clientPhones])];
          if (phonesToFetch.length > 0) {
            const { data: profilesByPhone } = await supabase
              .from('Profiles')
              .select('id, first_name, last_name, phone_number')
              .in('phone_number', phonesToFetch);

            if (profilesByPhone) {
              profilesByPhone.forEach(p => {
                if (p.id) profileMap.set(p.id, p);
                if (p.phone_number) phoneMap.set(p.phone_number, p);
              });
            }
          }
          
          // Enrich bookings with profile data
          const enriched = (bookingsData || []).map(booking => ({
            ...booking,
            client: booking.client_id
              ? [profileMap.get(booking.client_id)]
              : (booking.client_phone_number && phoneMap.has(booking.client_phone_number) ? [phoneMap.get(booking.client_phone_number)] : null),
            stylist: booking.stylist_profile_id
              ? [profileMap.get(booking.stylist_profile_id)]
              : (() => {
                  const phoneKey = booking.stylists_phone_number || booking.stylist_phone_number || booking.stylist_phone;
                  return phoneKey && phoneMap.has(phoneKey) ? [phoneMap.get(phoneKey)] : null;
                })(),
            stylist_name: (() => {
              const phoneKey = booking.stylists_phone_number || booking.stylist_phone_number || booking.stylist_phone;
              const stylistProfile = booking.stylist_profile_id
                ? profileMap.get(booking.stylist_profile_id)
                : (phoneKey ? phoneMap.get(phoneKey) : null);
              if (stylistProfile) return `${stylistProfile.first_name || ''} ${stylistProfile.last_name || ''}`.trim() || stylistProfile.phone_number || '';
              return phoneKey || '';
            })(),
          }));
          
          res = { data: enriched, error: null };
        }
      } else {
        res = { data: [], error: null };
      }
    } else if (tab.name === 'Closet Items') {
      // For Closet Items, fetch by phone_number
      res = await supabase
        .from('Closet Items')
        .select('*')
        .eq('phone_number', phone)
        .order('created_at',{ ascending:false });
    } else {
      res = await supabase
        .from(tab.name)
        .select('*')
        .eq('phone_number', phone)
        .order('created_at',{ ascending:false });
    }
    setLoading(false);
    if (res.error) console.error(res.error);
    else setRelatedRows(res.data || []);
  };

  // Fetch email history
  const fetchEmailLog = async profile_id => {
    const { data, error } = await supabase
      .from('email_logs')
      .select('*')
      .eq('profile_id', profile_id)
      .order('sent_at',{ ascending:false });
    if (error) console.error(error);
    else setEmailLog(data);
  };

  useEffect(() => { fetchProfile(); }, [id, fetchProfile]);

  const handleTabChange = (_e, newIndex) => {
    setTabIndex(newIndex);
    const tab = tabs[newIndex];
    if ((tab.type === 'table' || tab.type === 'chats') && profile?.phone_number) {
      fetchRelated(profile.phone_number, tab);
    } else if (tab.type === 'ratings' && profile?.phone_number && profile?.user_type === 'Stylist') {
      fetchRatings(profile.phone_number);
    } else if (tab.type === 'legal' && profile?.id && String(profile?.user_type || '').toLowerCase() === 'stylist') {
      fetchLegalDocs(profile.id);
    } else if (tab.type === 'payouts' && profile?.phone_number) {
      fetchPayouts(profile.phone_number);
    } else if (tab.type === 'refunds' && profile?.id) {
      fetchRefundableBookings(profile.id);
    } else if (tab.type === 'support') {
      fetchSupportTickets(profile?.phone_number, profile?.id);
    }
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {[...Array(5)].map((_, i) => (
          i < fullStars ? (
            <StarIcon key={i} sx={{ color: status.warning, fontSize: 20 }} />
          ) : i === fullStars && hasHalfStar ? (
            <StarIcon key={i} sx={{ color: status.warning, fontSize: 20, opacity: 0.5 }} />
          ) : (
            <StarBorderIcon key={i} sx={{ color: surface.line, fontSize: 20 }} />
          )
        ))}
        <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
          {rating.toFixed(1)}
        </Typography>
      </Box>
    );
  };

  // Send email via Graph and log
  const handleEmailSend = async () => {
    if (!isAuthenticated) {
      setSnack({ open:true, message:'Please sign in', severity:'error' });
      return;
    }
    try {
      let authResult;
      try {
        authResult = await instance.acquireTokenSilent({
          scopes: SCOPES, account: accounts[0]
        });
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          authResult = await instance.acquireTokenPopup({ scopes: SCOPES });
        } else {
          throw e;
        }
      }

      const payload = {
        message: {
          subject: emailForm.subject,
          body: { contentType:"HTML", content: emailForm.body },
          toRecipients: [{ emailAddress:{ address: emailForm.to } }],
          ccRecipients: emailForm.cc
            ? [{ emailAddress:{ address: emailForm.cc } }]
            : []
        }
      };

      const graphRes = await fetch(
        "https://graph.microsoft.com/v1.0/users/support@llpersonalshopping.com/sendMail",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authResult.accessToken}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );
      if (!graphRes.ok) {
        const errJson = await graphRes.json();
        throw new Error(errJson.error?.message || JSON.stringify(errJson));
      }

      await supabase.from('email_logs').insert([{
        profile_id: profile.id,
        recipient:  emailForm.to,
        subject:    emailForm.subject,
        body:       emailForm.body,
        sent_at:    new Date().toISOString()
      }]);
      fetchEmailLog(profile.id);

      setSnack({ open:true, message:'Email sent!', severity:'success' });
      setEmailForm({ ...emailForm, subject:'', body:'', cc:'' });
      setAttachments([]);
    } catch (err) {
      console.error(err);
      setSnack({ open:true, message:`Send failed: ${err.message}`, severity:'error' });
    }
  };

  // Save draft handler
  const handleSaveDraft = async () => {
    try {
      const { error } = await supabase
        .from('email_drafts')
        .upsert([{
          profile_id: profile.id,
          recipient:  emailForm.to,
          cc:         emailForm.cc,
          subject:    emailForm.subject,
          body:       emailForm.body,
          saved_at:   new Date().toISOString()
        }]);
      if (error) throw error;
      setSnack({ open:true, message:'Draft saved!', severity:'success' });
    } catch (err) {
      console.error(err);
      setSnack({ open:true, message:`Save draft failed: ${err.message}`, severity:'error' });
    }
  };

  if (loading || !profile) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', p:4 }}>
        <CircularProgress/>
      </Box>
    );
  }

  // Colors for history bars
  const barColors = [brand.gold,status.success,brand.goldDeep];

  const brands = toArray(profile.brands || profile.favorite_brands);
  const preferences = toArray(profile.preferences || profile.signature_style);
  const additionalImages = toArray(profile.additional_images);
  const normalizedAdditional = additionalImages.map(img => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    const clean = img.replace(/^additional_images\//, '').replace(/^\/+/, '');
    return buildPublicUrl('additional_images', clean);
  }).filter(Boolean);
  const mainAvatar = normalizeProfileImage(profile.profile_image) || profile.profile_image_url || normalizedAdditional[0] || null;
  const memberSince = profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const lastLogin = profile.last_login || profile.updated_at;
  const lastLoginText = lastLogin ? new Date(lastLogin).toLocaleString() : '—';
  const subscriptionPlanName = subscription?.plan?.display_name || subscription?.plan_name || 'Subscription';
  const subscriptionPriceRaw = subscription?.price ?? (subscription?.plan
    ? (subscription?.renewal_period === 'yearly' ? subscription.plan.yearly_price : subscription.plan.monthly_price)
    : null);
  const subscriptionPrice = subscriptionPriceRaw != null && !Number.isNaN(Number(subscriptionPriceRaw))
    ? Number(subscriptionPriceRaw)
    : null;
  const subscriptionRenewalDate = subscription?.end_date || subscription?.renewal_date || subscription?.start_date;
  const formattedRenewalDate = subscriptionRenewalDate
    ? new Date(subscriptionRenewalDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
  const renewalLabel = subscription?.renewal_period === 'yearly' ? 'yr' : 'mo';

  return (
    <Box component="main" sx={{ p: 0, bgcolor: surface.page, minHeight: '100vh' }}>
      {/* HEADER - Matching HTML design */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          mb: 0,
          bgcolor: 'white',
          borderBottom: `1px solid ${surface.line}`,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate(-1)}
              sx={{ color: ink[500], '&:hover': { bgcolor: surface.sunken } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 600, color: ink[900] }}>
              User Profile
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {String(profile.user_type || '').toLowerCase() === 'stylist' && (
              // Publishing is what decides whether clients see a stylist:
              // StylistFetcher filters on is_published. This button used to
              // write Profiles.stylist_approved_at, a column that does not
              // exist, so it could only ever fail — and even had it existed,
              // nothing read it. It goes through admin_set_stylist_status now,
              // the same path the Stylist approval page uses, so there is one
              // source of truth rather than two that disagree.
              <Button
                variant={profile.is_published ? 'outlined' : 'contained'}
                color={profile.is_published ? 'warning' : 'success'}
                size="small"
                onClick={async () => {
                  const publishing = !profile.is_published;
                  const { error } = await supabase.rpc('admin_set_stylist_status', {
                    p_profile_id: profile.id,
                    p_action: publishing ? 'publish' : 'unpublish',
                    p_reason: publishing ? null : 'Unpublished from the profile page',
                    p_override: false,
                  });
                  if (error) {
                    // The readiness gate and the signed-agreement trigger both
                    // refuse here, and their messages say which, so they are
                    // shown rather than flattened into "could not update".
                    setSnack({ open: true, message: error.message, severity: 'error' });
                  } else {
                    setProfile({ ...profile, is_published: publishing });
                    setSnack({
                      open: true,
                      message: publishing ? 'Stylist published and now listed' : 'Stylist unpublished; no longer listed',
                      severity: 'success',
                    });
                  }
                }}
              >
                {profile.is_published ? 'Unpublish' : 'Publish stylist'}
              </Button>
            )}
            {profile.phone_number && (
              <Button
                startIcon={isBlocked ? <UnblockIcon /> : <BlockIcon />}
                color={isBlocked ? 'success' : 'error'}
                variant="outlined"
                size="small"
                onClick={async () => {
                  if (isBlocked) {
                    const { error } = await supabase
                      .from('blocked_users')
                      .delete()
                      .eq('blocked_phone_number', profile.phone_number);
                    if (error) {
                      setSnack({ open:true, message:`Unblock failed: ${error.message}`, severity:'error' });
                    } else {
                      setIsBlocked(false);
                      setSnack({ open:true, message:'User unblocked', severity:'success' });
                    }
                  } else {
                    const { error } = await supabase
                      .from('blocked_users')
                      .insert([{
                        blocker_phone_number: 'admin',
                        blocked_phone_number: profile.phone_number,
                      }]);
                    if (error) {
                      if (error.code === '23505') {
                        setSnack({ open:true, message:'User is already blocked', severity:'warning' });
                      } else {
                        setSnack({ open:true, message:`Block failed: ${error.message}`, severity:'error' });
                      }
                    } else {
                      setIsBlocked(true);
                      setSnack({ open:true, message:'User blocked', severity:'success' });
                    }
                  }
                }}
              >
                {isBlocked ? 'Unblock' : 'Block'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* MAIN CONTENT */}
      <Box sx={{ p: 4 }}>

      {/* SIMPLIFIED HEADER - Name, Phone, User Type Only */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${surface.line}`,
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 3
        }}
      >
        {mainAvatar && (
          <Avatar
            src={mainAvatar}
            sx={{
              width: 64,
              height: 64,
              bgcolor: ink[900],
              fontSize: '1.5rem',
            }}
          >
            {profile.first_name?.[0] || profile.last_name?.[0] || '?'}
          </Avatar>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: ink[900], mb: 0.5 }}>
            {(profile.first_name || '') + ' ' + (profile.last_name || '')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {profile.phone_number && (
              <Typography variant="body1" sx={{ color: ink[500] }}>
                {profile.phone_number}
              </Typography>
            )}
            {profile.user_type && (
              <Chip
                label={prettyStatus(profile.user_type)}
                size="small"
                sx={{ bgcolor: brand.goldWash, color: brand.gold, fontWeight: 600 }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* COLLAPSIBLE SECTIONS */}
      <Box sx={{ mb: 2 }}>
        {/* Profile Picture & Additional Images */}
        {(mainAvatar || normalizedAdditional.length > 0) && (
          <Accordion sx={{ mb: 1, border: `1px solid ${surface.line}`, borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ink[900] }}>
                Profile Images
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {mainAvatar && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: ink[500], mb: 1, fontWeight: 600 }}>
                    Profile Picture
                  </Typography>
                  <Avatar
                    src={mainAvatar}
                    sx={{
                      width: 120,
                      height: 120,
                      border: `2px solid ${surface.line}`,
                      bgcolor: ink[900],
                      fontSize: '2rem',
                    }}
                  >
                    {profile.first_name?.[0] || profile.last_name?.[0] || '?'}
                  </Avatar>
                </Box>
              )}
              {normalizedAdditional.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ color: ink[500], mb: 1, fontWeight: 600 }}>
                    Additional Images
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {normalizedAdditional.map((img, idx) => (
                      <Box
                        key={idx}
                        component="img"
                        src={img}
                        alt={`Additional image ${idx + 1}`}
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: `1px solid ${surface.line}`,
                          bgcolor: surface.page,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8,
                            transform: 'scale(1.05)',
                            transition: 'all 0.2s'
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Anything given to this person off their next booking. */}
        {profile.phone_number && <ProfileCourtesy phoneNumber={profile.phone_number} userType={profile.user_type} />}

        {/* Services, availability, reports and cancellation rate — §34, §35. */}
        {profile.phone_number && <ProfileVetting phoneNumber={profile.phone_number} userType={profile.user_type} />}

        {/* User Details */}
        <Accordion sx={{ mb: 1, border: `1px solid ${surface.line}`, borderRadius: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ink[900] }}>
              User Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Full Name" secondary={`${profile.first_name || ''} ${profile.last_name || ''}`.trim() || '—'} />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="User ID" secondary={profile.id || '—'} />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Email" secondary={profile.email || '—'} />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Phone" secondary={profile.phone_number || '—'} />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="User Type" secondary={profile.user_type || '—'} />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Member Since" secondary={memberSince} />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Last Login" secondary={lastLoginText} />
              </ListItem>
              {profile.date_of_birth && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="Date of Birth" secondary={new Date(profile.date_of_birth).toLocaleDateString()} />
                </ListItem>
              )}
              {profile.gender && (
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText primary="Gender" secondary={profile.gender} />
                </ListItem>
              )}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Subscription */}
        <Accordion sx={{ mb: 1, border: `1px solid ${surface.line}`, borderRadius: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ink[900] }}>
              Subscription
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {subscription ? (
              <Box sx={{ bgcolor: surface.page, border: `1px solid ${surface.line}`, borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: ink[900] }}>
                      {subscriptionPlanName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: ink[500] }}>
                      {(subscription.status || 'active').toUpperCase()} · {(subscription.renewal_period || '—').toUpperCase()}
                    </Typography>
                  </Box>
                  {typeof subscription.auto_renew === 'boolean' && (
                    <Chip
                      label={subscription.auto_renew ? 'Auto-Renew On' : 'Auto-Renew Off'}
                      size="small"
                      sx={{
                        bgcolor: subscription.auto_renew ? brand.goldWash : status.warningBg,
                        color: subscription.auto_renew ? brand.goldDeep : status.warning,
                        fontWeight: 600
                      }}
                    />
                  )}
                </Box>
                {subscriptionPrice != null && (
                  <Typography variant="body2" sx={{ color: ink[700], fontWeight: 600 }}>
                    ${subscriptionPrice.toFixed(2)}/{renewalLabel}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: ink[500], mt: 0.5 }}>
                  Renews on {formattedRenewalDate}
                </Typography>
                {subscription.transaction_id && (
                  <Typography variant="caption" sx={{ color: ink[300] }}>
                    Transaction #{subscription.transaction_id}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: ink[500] }}>
                No subscription on file
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Stats */}
        <Accordion sx={{ mb: 1, border: `1px solid ${surface.line}`, borderRadius: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ink[900] }}>
              Statistics
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${surface.line}`, bgcolor: surface.page }}>
                  <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>Lifetime Value</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: ink[900] }}>
                    ${stats.lifetimeValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${surface.line}`, bgcolor: surface.page }}>
                  <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>Total Bookings</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: ink[900] }}>
                    {stats.totalBookings}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${surface.line}`, bgcolor: surface.page }}>
                  <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>Closet Items</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: ink[900] }}>
                    {stats.closetItems}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Preferences & Brands */}
        {(brands.length > 0 || preferences.length > 0) && (
          <Accordion sx={{ mb: 1, border: `1px solid ${surface.line}`, borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ink[900] }}>
                Preferences & Brands
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {brands.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: ink[500], fontWeight: 600, mb: 1 }}>
                    Favorite Brands
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {brands.map((b, idx) => (
                      <Chip key={idx} label={b} size="small" sx={{ bgcolor: surface.sunken, color: ink[700] }} />
                    ))}
                  </Box>
                </Box>
              )}
              {preferences.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ color: ink[500], fontWeight: 600, mb: 1 }}>
                    Style / Preferences
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {preferences.map((p, idx) => (
                      <Chip key={idx} label={p} size="small" sx={{ bgcolor: brand.goldWash, color: brand.goldDeep }} />
                    ))}
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Additional Profile Fields */}
        {(profile.about_me || profile.expertise || profile.preferred_location || profile.years_of_experiences) && (
          <Accordion sx={{ mb: 1, border: `1px solid ${surface.line}`, borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: ink[900] }}>
                Additional Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List dense>
                {profile.about_me && (
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText primary="About Me" secondary={profile.about_me} />
                  </ListItem>
                )}
                {profile.expertise && (
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText primary="Expertise" secondary={profile.expertise} />
                  </ListItem>
                )}
                {profile.preferred_location && (
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText primary="Preferred Location" secondary={profile.preferred_location} />
                  </ListItem>
                )}
                {profile.years_of_experiences && (
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemText primary="Years of Experience" secondary={profile.years_of_experiences} />
                  </ListItem>
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      {/* TABS */}
      <Paper variant="outlined" sx={{ mb:2 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          {tabs.map((t,i) =>
            t.type === 'email'
              ? <Tab key="email" icon={<EmailIcon/>} label="Send Email"/>
              : <Tab key={t.label} label={t.label}/>
          )}
        </Tabs>
      </Paper>

      {tabIndex === emailTabIndex ? (
        <Grid container spacing={2}>
          {/* Left Column */}
          <Grid item xs={12} md={4}>
            {/* Email History */}
            <Card variant="outlined" sx={{ mb:2 }}>
              <CardContent>
                <Box sx={{ display:'flex', justifyContent:'space-between', mb:1 }}>
                  <Typography variant="subtitle1">Email History</Typography>
                  <Button size="small">View All</Button>
                </Box>
                <List dense>
                  {emailLog.slice(0,3).map((e,i) => (
                    <React.Fragment key={i}>
                      <ListItem sx={{ borderLeft: `4px solid ${barColors[i]}`, pl:1 }}>
                        <ListItemText
                          primary={e.subject}
                          secondary={(
                            <>
                              <Typography component="span" variant="body2" color="textSecondary">
                                {new Date(e.sent_at).toLocaleDateString('en-US',{
                                  month:'short', day:'numeric', year:'numeric'
                                })} – Sent
                              </Typography>
                              <br/>
                              <Typography component="span" variant="body2" noWrap>
                                {e.body.replace(/<[^>]+>/g,'').slice(0,50)}…
                              </Typography>
                            </>
                          )}
                        />
                      </ListItem>
                      {i < 2 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Contact Us History */}
            <Card variant="outlined" sx={{ mb:2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Contact Us History
                </Typography>
                <ContactUsWidget phoneNumber={profile.phone_number}/>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Send Email */}
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p:2 }}>
              <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
                <Chip label="Draft Saved" color="info" size="small"/>
                <Button variant="outlined" endIcon={<RefreshIcon sx={{ transform:'rotate(90deg)' }}/>}>
                  Dropdown button
                </Button>
              </Box>

              <TextField
                label="To"
                fullWidth
                margin="normal"
                value={emailForm.to}
                onChange={e => setEmailForm(f => ({ ...f, to: e.target.value }))}
              />
              <TextField
                label="Add CC recipients"
                fullWidth
                margin="normal"
                value={emailForm.cc}
                onChange={e => setEmailForm(f => ({ ...f, cc: e.target.value }))}
              />
              <TextField
                label="Enter email subject"
                fullWidth
                margin="normal"
                value={emailForm.subject}
                onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))}
              />

              <Box sx={{ display:'flex', gap:1, my:1 }}>
                <IconButton size="small"><FormatBoldIcon/></IconButton>
                <IconButton size="small"><FormatItalicIcon/></IconButton>
                <IconButton size="small"><FormatUnderlinedIcon/></IconButton>
                <IconButton size="small"><FormatListBulletedIcon/></IconButton>
                <IconButton size="small"><LinkIcon/></IconButton>
              </Box>

              <TextField
                label="Compose your message here..."
                fullWidth
                margin="normal"
                multiline
                rows={6}
                value={emailForm.body}
                onChange={e => setEmailForm(f => ({ ...f, body: e.target.value }))}
              />

              <Box sx={{ border:'1px dashed', borderColor:'divider', p:2, textAlign:'center', my:1 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AttachFileIcon/>}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={e => {
                      const files = Array.from(e.target.files);
                      setAttachments(prev => [...prev, ...files]);
                    }}
                  />
                </Button>
                {attachments.length > 0 && (
                  <List dense sx={{ mt:1 }}>
                    {attachments.map((file,i) => (
                      <ListItem
                        key={i}
                        secondaryAction={
                          <IconButton edge="end" onClick={() =>
                            setAttachments(prev => prev.filter((_, idx) => idx !== i))
                          }>
                            <CloseIcon fontSize="small"/>
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <InsertDriveFileIcon fontSize="small"/>
                        </ListItemIcon>
                        <ListItemText primary={file.name}/>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              <FormControlLabel
                control={<Checkbox size="small"/>}
                label="Save as template"
              />

              <Box sx={{ display:'flex', justifyContent:'flex-end', gap:1, mt:2 }}>
                <Button variant="outlined" startIcon={<DraftsIcon />} onClick={handleSaveDraft}>
                  Save Draft
                </Button>
                <Button variant="contained" startIcon={<SendIcon />} onClick={handleEmailSend}>
                  Send Email
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        /* NON-EMAIL TABS */
        <Box sx={{ mt:2 }}>
          {/* PROFILE INFO - Redesigned to match HTML */}
          {tabs[tabIndex].type === 'info' && (
            <Grid container spacing={3}>
              {/* Left Column - Profile Summary, User Details, Subscription */}
              <Grid item xs={12} lg={5}>
                {/* Profile Summary Card */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    border: `1px solid ${surface.line}`,
                    textAlign: 'center'
                  }}
                >
                  <Avatar
                    src={profile.profile_image_url || profile.profile_image}
                    sx={{ 
                      width: 96, 
                      height: 96, 
                      mx: 'auto', 
                      mb: 2,
                      border: `4px solid ${surface.sunken}`,
                      bgcolor: surface.line
                    }}
                  >
                    {profile.first_name?.[0] || ''}{profile.last_name?.[0] || ''}
                  </Avatar>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {profile.first_name} {profile.last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: ink[500], mb: 1 }}>
                    {profile.email}
                  </Typography>
                  <Chip 
                    label={isBlocked ? 'Blocked' : 'Active'} 
                    size="small"
                    color={isBlocked ? 'error' : 'success'}
                    sx={{ 
                      bgcolor: isBlocked ? status.dangerBg : status.successBg,
                      color: isBlocked ? status.danger : status.success,
                      mb: 2,
                      fontWeight: 600
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      sx={{
                        bgcolor: ink[900],
                        color: 'white',
                        '&:hover': { bgcolor: ink[900] },
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Edit Profile
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<MessageIcon />}
                      sx={{
                        borderColor: surface.line,
                        color: ink[700],
                        '&:hover': { borderColor: surface.line, bgcolor: surface.page },
                        textTransform: 'none',
                        fontWeight: 600
                      }}
                    >
                      Send Message
                    </Button>
                  </Box>
                </Paper>

                {/* User Details Card */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    border: `1px solid ${surface.line}`
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    User Details
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: ink[500] }}>Full Name:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {profile.first_name} {profile.last_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: ink[500] }}>User ID:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: ink[500] }}>
                        #{profile.id?.slice(0, 8).toUpperCase() || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: ink[500] }}>Phone:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {profile.phone_number || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: ink[500] }}>Member Since:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: ink[500] }}>Last Login:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {profile.last_login ? new Date(profile.last_login).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'Never'}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Subscription Card */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    border: `1px solid ${surface.line}`
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Subscription
                  </Typography>
                  {subscription ? (
                    <Box sx={{ bgcolor: surface.page, border: `1px solid ${surface.line}`, borderRadius: 2, p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            {subscriptionPlanName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: ink[500] }}>
                            {(subscription.status || 'active').toUpperCase()} · {(subscription.renewal_period || '—').toUpperCase()}
                          </Typography>
                        </Box>
                        {typeof subscription.auto_renew === 'boolean' && (
                          <Chip 
                            label={subscription.auto_renew ? 'Auto-Renew On' : 'Auto-Renew Off'} 
                            size="small"
                            sx={{ 
                              bgcolor: subscription.auto_renew ? brand.goldWash : status.warningBg,
                              color: subscription.auto_renew ? brand.goldDeep : status.warning,
                              fontWeight: 600 
                            }}
                          />
                        )}
                      </Box>
                      {subscriptionPrice != null && (
                        <Typography variant="body2" sx={{ color: ink[700], fontWeight: 600, mb: 0.5 }}>
                          ${subscriptionPrice.toFixed(2)}/{renewalLabel}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: ink[500], mb: 1.5 }}>
                        Renews on {formattedRenewalDate}
                      </Typography>
                      {subscription.transaction_id && (
                        <Typography variant="caption" sx={{ color: ink[300], display: 'block' }}>
                          Transaction #{subscription.transaction_id}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: ink[500] }}>
                      No active subscription
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Right Column - Stats, Chart, Recent Bookings */}
              <Grid item xs={12} lg={7}>
                {/* Stats Overview */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        border: `1px solid ${surface.line}`
                      }}
                    >
                      <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
                        Lifetime Value
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        ${stats.lifetimeValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        border: `1px solid ${surface.line}`
                      }}
                    >
                      <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
                        Total Bookings
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.totalBookings}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        border: `1px solid ${surface.line}`
                      }}
                    >
                      <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
                        Closet Items
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {stats.closetItems}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Booking Activity Chart */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    border: `1px solid ${surface.line}`
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Booking Activity
                    </Typography>
                    <select 
                      style={{
                        fontSize: '0.875rem',
                        border: `1px solid ${surface.line}`,
                        borderRadius: '6px',
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option>Last 6 Months</option>
                      <option>Last 12 Months</option>
                    </select>
                  </Box>
                  <Box sx={{ width: '100%', height: 250 }}>
                    <ResponsiveContainer>
                      <AreaChart data={bookingActivityData}>
                        <defs>
                          <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={brand.goldDeep} stopOpacity={0.1}/>
                            <stop offset="95%" stopColor={brand.goldDeep} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={surface.line} />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fill: ink[500], fontSize: 12 }}
                          stroke={surface.line}
                        />
                        <YAxis 
                          tick={{ fill: ink[500], fontSize: 12 }}
                          stroke={surface.line}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: `1px solid ${surface.line}`,
                            borderRadius: '6px'
                          }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="bookings" 
                          stroke={brand.goldDeep} 
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorBookings)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>

                {/* Recent Bookings Table */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    borderRadius: 3,
                    border: `1px solid ${surface.line}`,
                    overflow: 'hidden'
                  }}
                >
                  <Box sx={{ p: 3, borderBottom: `1px solid ${surface.line}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Recent Bookings
                    </Typography>
                    <Button 
                      size="small"
                      sx={{ 
                        color: brand.gold,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': { color: brand.goldDeep }
                      }}
                    >
                      View All
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: surface.page }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Booking ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Stylist
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Amount
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                            Status
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentBookings.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: ink[500] }}>
                              No bookings found
                            </TableCell>
                          </TableRow>
                        ) : (
                          recentBookings.map((booking, idx) => (
                            <TableRow key={booking.id || idx} hover>
                              <TableCell sx={{ fontFamily: 'monospace', color: ink[700] }}>
                                #{booking.id?.slice(0, 8).toUpperCase() || 'N/A'}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                {booking.stylist ? `${booking.stylist.first_name} ${booking.stylist.last_name}` : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {booking.start_ts ? new Date(booking.start_ts).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                }) : 'N/A'}
                              </TableCell>
                              <TableCell>
                                ${parseFloat(booking.amount || booking.price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={prettyStatus(booking.status) || 'Pending'}
                                  size="small"
                                  sx={{
                                    bgcolor: booking.status === 'Completed' ? status.successBg : 
                                             booking.status === 'Cancelled' ? status.dangerBg : status.warningBg,
                                    color: booking.status === 'Completed' ? status.success : 
                                           booking.status === 'Cancelled' ? status.danger : status.warning,
                                    fontWeight: 500,
                                    fontSize: '0.75rem'
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* CONTACT US */}
          {tabs[tabIndex].type === 'table' && tabs[tabIndex].name === 'Contact Us' && (
            <ContactUsWidget phoneNumber={profile.phone_number}/>
          )}

          {/* RATINGS TAB */}
          {tabs[tabIndex].type === 'ratings' && (
            <Box>
              {profile.user_type !== 'Stylist' ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    Ratings are only available for stylists.
                  </Typography>
                </Paper>
              ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : ratings.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">No ratings found</Typography>
                </Paper>
              ) : (
                <Box>
                  {/* Average Rating Summary */}
                  <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: `1px solid ${surface.line}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: ink[500], mb: 1 }}>
                          Average Rating
                        </Typography>
                        {renderStars(averageRating)}
                        <Typography variant="body2" sx={{ color: ink[500], mt: 1 }}>
                          Based on {ratings.length} review{ratings.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Individual Ratings */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {ratings.map((rating, idx) => (
                      <Paper key={rating.id || idx} sx={{ p: 3, borderRadius: 3, border: `1px solid ${surface.line}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          {rating.client?.profile_image_url ? (
                            <Avatar
                              src={rating.client.profile_image_url}
                              sx={{ width: 48, height: 48 }}
                            >
                              {rating.client.first_name?.[0] || '?'}
                            </Avatar>
                          ) : (
                            <Avatar sx={{ width: 48, height: 48, bgcolor: 'grey.300' }}>
                              {rating.client?.first_name?.[0] || '?'}
                            </Avatar>
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {rating.client 
                                    ? `${rating.client.first_name} ${rating.client.last_name}`.trim() 
                                    : rating.phone_number || 'Anonymous'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: ink[500] }}>
                                  {rating.created_at 
                                    ? new Date(rating.created_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })
                                    : 'N/A'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {renderStars(parseFloat(rating.rating) || 0)}
                              </Box>
                            </Box>
                            {rating.review && (
                              <Typography variant="body2" sx={{ color: ink[700], mt: 1 }}>
                                {rating.review}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}


          {/* PAYOUTS TAB */}
          {tabs[tabIndex].type === 'payouts' && (
            <Box>
              {String(profile.user_type || '').toLowerCase() !== 'stylist' ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">Payouts are only available for stylists.</Typography>
                </Paper>
              ) : (
                <>
                  <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Earnings Balance</Typography>
                      {defaultPayoutMethod ? (
                        <Chip
                          size="small"
                          label={
                            defaultPayoutMethod.method_type === 'stripe_connect'
                              ? (defaultPayoutMethod.is_verified ? 'Payout Setup: Stripe Connect' : 'Payout Setup: Stripe Connect (Pending Verification)')
                              : `Payout Setup: ${defaultPayoutMethod.method_type === 'bank_account' ? 'Bank account' : prettyStatus(defaultPayoutMethod.provider || defaultPayoutMethod.method_type)}`
                          }
                          color={defaultPayoutMethod.method_type === 'stripe_connect' && !defaultPayoutMethod.is_verified ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      ) : (
                        <Chip size="small" label="Payout Setup: Not Connected" color="default" variant="outlined" />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: defaultPayoutMethod || earningsSummary.available > 0 ? 2 : 0 }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Available</Typography>
                        <Typography variant="h6" sx={{ color: status.success }}>${earningsSummary.available.toFixed(2)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Processing</Typography>
                        <Typography variant="h6">${earningsSummary.processing.toFixed(2)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Paid</Typography>
                        <Typography variant="h6">${earningsSummary.paid.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                    {payoutActionError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPayoutActionError(null)}>{payoutActionError}</Alert>
                    )}
                    {earningsSummary.available > 0 && (
                      !defaultPayoutMethod ? (
                        <Typography variant="body2" color="textSecondary">
                          No active payout method on file — the stylist needs to connect one before you can pay them out.
                        </Typography>
                      ) : (
                        <Button
                          variant="contained"
                          disabled={payoutActionLoading}
                          onClick={handlePayNow}
                          sx={{ bgcolor: ink[900], textTransform: 'none', '&:hover': { bgcolor: ink[900] } }}
                        >
                          {payoutActionLoading ? 'Processing…' : `Pay Out $${earningsSummary.available.toFixed(2)} Now`}
                        </Button>
                      )
                    )}
                  </Paper>

                  {payouts.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">No payout requests found.</Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payouts.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            <Chip 
                              label={prettyStatus(p.status)} 
                              size="small" 
                              color={p.status === 'approved' || p.status === 'completed' ? 'success' : p.status === 'rejected' ? 'error' : 'warning'} 
                              variant="outlined" 
                            />
                          </TableCell>
                          <TableCell>${parseFloat(p.total_amount || p.amount || 0).toFixed(2)}</TableCell>
                          <TableCell>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>{p.payout_method_id ? 'Linked Method' : 'Manual'}</TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {p.admin_notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                  )}
                </>
              )}
            </Box>
          )}

          {/* REFUNDS TAB */}
          {tabs[tabIndex].type === 'refunds' && (
            <Box>
              {String(profile.user_type || '').toLowerCase() === 'stylist' ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">Refunds apply to client bookings, not stylist profiles.</Typography>
                </Paper>
              ) : (
                <>
                  {refundActionError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRefundActionError(null)}>{refundActionError}</Alert>
                  )}
                  {refundableBookings.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="textSecondary">No paid bookings found for this client.</Typography>
                    </Paper>
                  ) : (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {refundableBookings.map((b) => (
                            <TableRow key={b.id} hover>
                              <TableCell>{b.service_title || 'N/A'}</TableCell>
                              <TableCell>{b.start_ts ? new Date(b.start_ts).toLocaleDateString() : '-'}</TableCell>
                              <TableCell>${parseFloat(b.price || 0).toFixed(2)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={prettyStatus(b.status)}
                                  size="small"
                                  color={b.status === 'Refunded' ? 'default' : b.status === 'Completed' ? 'success' : 'warning'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                {b.status === 'Refunded' ? (
                                  <Typography variant="caption" color="textSecondary">Refunded</Typography>
                                ) : (
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    disabled={refundActionLoading === b.id}
                                    onClick={() => handleIssueRefund(b.id)}
                                  >
                                    {refundActionLoading === b.id ? 'Refunding…' : 'Issue Refund'}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          )}

          {/* SUPPORT TICKETS TAB */}
          {tabs[tabIndex].type === 'support' && (
            <Box>
              {supportTickets.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">No support tickets found.</Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Priority</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supportTickets.map((t) => (
                        <TableRow key={t.id} hover>
                          <TableCell>
                            <Chip 
                              label={t.priority} 
                              size="small" 
                              color={t.priority === 'urgent' ? 'error' : t.priority === 'high' ? 'warning' : 'default'} 
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={prettyStatus(t.status)} 
                              size="small" 
                              variant="outlined"
                              color={t.status === 'resolved' || t.status === 'closed' ? 'success' : 'primary'}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{t.subject}</TableCell>
                          <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                          <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {t.message_body || t.body || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* LEGAL DOCS TAB */}
          {tabs[tabIndex].type === 'legal' && (
            <Box>
              {String(profile.user_type || '').toLowerCase() !== 'stylist' ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">Legal docs are only tracked for stylist accounts.</Typography>
                </Paper>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Compliance Summary</Typography>
                    {legalCompliance ? (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={`Agreement: ${prettyStatus(legalCompliance.agreement_status) || '-'}`} color="info" variant="outlined" />
                        <Chip label={`Identity: ${prettyStatus(legalCompliance.identity_status) || '-'}`} variant="outlined" />
                        <Chip label={`Payout: ${prettyStatus(legalCompliance.payout_status) || '-'}`} color="warning" variant="outlined" />
                        <Chip label={`Updated: ${legalCompliance.updated_at ? new Date(legalCompliance.updated_at).toLocaleString() : 'N/A'}`} size="small" />
                      </Box>
                    ) : (
                      <Typography color="text.secondary">No compliance row found yet.</Typography>
                    )}
                  </Paper>

                  <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>Documents Sent / Signed</Typography>
                    {legalDocs.length === 0 ? (
                      <Typography color="text.secondary">No legal documents found.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Agreement</TableCell>
                              <TableCell>Version</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Sent</TableCell>
                              <TableCell>Viewed</TableCell>
                              <TableCell>Signed</TableCell>
                              <TableCell>Provider</TableCell>
                              <TableCell>Document</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {legalDocs.map((doc) => {
                              // Signed on click rather than built as a public
                              // URL: the PDF carries the signer's name, email
                              // and IP, and the bucket does not check who is
                              // asking for a public object.
                              const hasDoc = Boolean(doc.signed_pdf_path);
                              return (
                                <TableRow key={doc.id} hover>
                                  <TableCell>{String(doc.agreement_type || '').replaceAll('_', ' ')}</TableCell>
                                  <TableCell>{doc.agreement_version || '-'}</TableCell>
                                  <TableCell><Chip label={prettyStatus(doc.status) || '-'} size="small" /></TableCell>
                                  <TableCell>{doc.sent_at ? new Date(doc.sent_at).toLocaleString() : '-'}</TableCell>
                                  <TableCell>{doc.viewed_at ? new Date(doc.viewed_at).toLocaleString() : '-'}</TableCell>
                                  <TableCell>{doc.signed_at ? new Date(doc.signed_at).toLocaleString() : '-'}</TableCell>
                                  <TableCell>{doc.provider || '-'}</TableCell>
                                  <TableCell>
                                    {hasDoc ? <Button size="small" onClick={() => openLegalDoc(doc.signed_pdf_path)}>View</Button> : '-'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {/* OTHER TABLES & CHATS */}
          {(tabs[tabIndex].type === 'table' || tabs[tabIndex].type === 'chats') &&
           tabs[tabIndex].name !== 'Contact Us' && (
            <Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : relatedRows.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">No {tabs[tabIndex].name || 'data'} found</Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {relatedRows[0] && Object.keys(relatedRows[0]).map(c =>
                          <TableCell key={c}>{c.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {relatedRows.map((r,i) => (
                        <TableRow key={r.id || i} hover>
                          {Object.entries(r).map(([key, val], j) => {
                            // Handle image URLs
                            if (typeof val === 'string' && /\.(jpe?g|png|gif|svg)$/i.test(val)) {
                              return (
                                <TableCell key={j}>
                                  <Box component="img" src={val} alt="" sx={{ maxHeight: 60, maxWidth: 100, objectFit: 'cover' }} />
                                </TableCell>
                              );
                            }
                            // Handle photo_url, image_url, image fields
                            if ((key === 'photo_url' || key === 'image_url' || key === 'image') && val) {
                              return (
                                <TableCell key={j}>
                                  <Box component="img" src={val} alt="" sx={{ maxHeight: 60, maxWidth: 100, objectFit: 'cover' }} />
                                </TableCell>
                              );
                            }
                            // Handle arrays/objects
                            let displayVal = val;
                            if (val == null) displayVal = '-';
                            else if (Array.isArray(val)) displayVal = val.length > 0 ? val.join(', ') : '-';
                            else if (typeof val === 'object') displayVal = JSON.stringify(val);
                            else displayVal = String(val);
                            
                            return (
                              <TableCell
                                key={j}
                                sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {displayVal}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      )}

      </Box>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
        onClose={() => setSnack(s => ({ ...s, open:false }))}
      >
        <Alert severity={snack.severity} sx={{ width:'100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
